import { CountryCode } from 'libphonenumber-js';
import { getE164PhoneNumber } from '@/utils/phone-number';
import { MICRO_CONVERSION } from '@/utils/money';
import { parseTransientAwareNumber } from '@/experiences/sales-portal/domain/pricing/screens/management/blocs/utils/numeric-input';
import { ProspectDetailsUpdateInput } from '@/types';
import { ProspectEditFormValues } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/types';

export interface BuildProspectDetailsUpdateParams {
  editedFields: Partial<Record<keyof ProspectEditFormValues, boolean>>;
  phoneCountryCode: CountryCode;
  values: ProspectEditFormValues;
}

const toTrimmedOrNull = (value: string): string | null => value.trim() || null;

const toIncumbentsOrNull = (value: string): string[] | null => {
  const incumbents = value
    .split(',')
    .map((incumbent) => incumbent.trim())
    .filter(Boolean);
  return incumbents.length > 0 ? incumbents : null;
};

const toMicrosOrNull = (value: string): number | null => {
  const parsed = parseTransientAwareNumber(value);
  return parsed === null ? null : Math.round(parsed * MICRO_CONVERSION);
};

// The prospect details close date is a plain calendar date (YYYY-MM-DD) with no timezone.
// It's converted via local date components rather than moment to prevent
// moment from rebasing to the restaurant's timezone, causing a shift in date
const toIsoDateOrNull = (value: Date | null): string | null =>
  value
    ? [
        String(value.getFullYear()),
        String(value.getMonth() + 1).padStart(2, '0'),
        String(value.getDate()).padStart(2, '0'),
      ].join('-')
    : null;

/**
 * Builds the payload for the `updateProspectDetails` mutation.
 *
 * The payload contains only fields that the user has edited.
 * Cleared fields are sent as null, and unedited fields are omitted.
 *
 * @param params.editedFields - Fields the user has edited since hydration (validated by the form)
 * @param params.phoneCountryCode - Country code used for phone number formatting
 * @param params.values - Current form values
 * @returns {@link ProspectDetailsUpdateInput}
 */
export const buildProspectDetailsUpdate = ({
  editedFields,
  phoneCountryCode,
  values,
}: BuildProspectDetailsUpdateParams): ProspectDetailsUpdateInput => {
  const update: ProspectDetailsUpdateInput = {};
  if (editedFields.contactName) {
    update.contact_name = toTrimmedOrNull(values.contactName);
  }
  if (editedFields.contactRole) {
    update.contact_role = toTrimmedOrNull(values.contactRole);
  }
  if (editedFields.contactEmail) {
    update.contact_email = toTrimmedOrNull(values.contactEmail);
  }
  if (editedFields.contactPhone) {
    update.contact_phone = getE164PhoneNumber(
      values.contactPhone,
      phoneCountryCode,
    );
  }
  if (editedFields.competitors) {
    update.incumbents = toIncumbentsOrNull(values.competitors);
  }
  if (editedFields.closeDate) {
    update.estimated_close_date = toIsoDateOrNull(values.closeDate);
  }
  if (editedFields.weeklyValue) {
    update.weekly_estimated_value_in_micros = toMicrosOrNull(
      values.weeklyValue,
    );
  }

  return update;
};
